import {ReactNode, useContext, useMemo, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {AxiosError, HttpStatusCode} from 'axios';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import {StandardSemiBoldTypography, StandardTypography,} from 'patient-ping-remedy/packages/typography';
import Modal from 'patient-ping-remedy/packages/modal';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {
  Attestation,
  AttestationStatus,
  attestationStatusDisplayValues,
  CareJourneyDetailsResponse,
  TaskStepName,
} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAuthStore} from '@/store/auth_store';
import {useAlertStore} from '@/store/alert_store';
import {useFeatureToggles} from '@/hooks/useFeatureToggles';
import {useMixpanel} from '@/hooks/useMixpanel';
import {MixpanelEventType, MixpanelTaskEvent} from '@/api/dto/mixpanel';
import {Feature, FeatureNew, FeaturePermissionLevel} from '@/api/dto/permissions';
import {FeatureToggle} from '@/api/dto/dto';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import {Section} from '@/components/pages/tasks/task_edit/progress/TaskEditDetails.styles';
import {
  AttestButton,
  CancelButton,
  CompletedDetailsWrapper,
  DetailLabel,
  DetailsCard,
  DetailsGrid,
  DetailsTitle,
  LockIcon,
  ModalContent,
  SectionContent,
  SectionHeaderRow,
  SectionTitleRow,
  StandardSmallTypography,
  StatusBadge,
  StatusIcon,
  StatusText,
  SubTextWrapper,
} from '@/components/pages/tasks/task_edit/progress/CareNoteSection.styles';
import CareNoteDeliveryStatus from "@/components/pages/tasks/task_edit/progress/CareNoteDeliveryStatus";
import Spacer from "patient-ping-remedy/packages/spacer";
import CareNotePreview from "@/components/pages/tasks/task_edit/progress/CareNotePreview";

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
}

const SEND_CARE_NOTE_LABEL = 'Send Care note';

function getAttestationStatus(careJourneyDetails: CareJourneyDetailsResponse): AttestationStatus {
  if (!isAttestationRequired(careJourneyDetails)) {
    return AttestationStatus.NOT_REQUIRED;
  }

  if (careJourneyDetails.orchestrationEngineDetails?.attestation?.status) {
    return careJourneyDetails.orchestrationEngineDetails.attestation.status;
  }

  const scheduledAppointmentStep = careJourneyDetails.steps.find(
    step => step.stepName === TaskStepName.SCHEDULED_APPOINTMENT
  );

  if (scheduledAppointmentStep?.status?.toLowerCase() === 'completed') {
    return AttestationStatus.PENDING;
  }

  return AttestationStatus.NOT_READY;
}

function isAttestationRequired(careJourneyDetails: CareJourneyDetailsResponse): boolean {
  return careJourneyDetails.eventSetting === 'Inpatient';
}

function getStatusBadgeColors(status: AttestationStatus) {
  switch (status) {
  case AttestationStatus.COMPLETED:
    return { bg: colors.green1, text: colors.charcoal, iconColor: 'var(--icon-success, #32923C)' };
  case AttestationStatus.PENDING:
    return { bg: colors.yellow1, text: colors.charcoal, iconColor: colors.yellow5 };
  case AttestationStatus.NOT_REQUIRED:
    return { bg: colors.gray1, text: colors.gray5, iconColor: colors.gray5 };
  case AttestationStatus.NOT_READY:
  default:
    return { bg: colors.gray1, text: colors.gray5, iconColor: colors.gray5 };
  }
}

function buildAttestationMessage(attestation: Attestation): ReactNode {
  if (!attestation.attestedBy) {
    return null;
  }

  const formattedDate = attestation.attestedAt ? DisplayHelpers.formatDateTime(attestation.attestedAt) : '';
  const title = attestation.attestedBy.title;
  const fullName = `${attestation.attestedBy.firstName} ${attestation.attestedBy.lastName}`;

  return (
    <>
      Reviewed and attested by {fullName}
      {title ? `, ${title}` : null}
      {formattedDate ? ` on ${formattedDate}` : null}
    </>
  );
}

export default function CareNoteSection({ careJourneyDetails }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const currentRoster = useActiveRoster();
  const { currentUser } = useAuthStore();
  const { addAlert } = useAlertStore();
  const { checkBooleanToggle } = useFeatureToggles();
  const { mixpanelEventProperties, getCurrentGroupDetails } = useMixpanel();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);


  const canManageTasks = useMemo(() => {
    if (!currentUser || !currentRoster) return false;
    return isNewPermissionsEnabled
      ? PermissionsHelpers.canForUserFeaturePermissionRosterNew(
        currentUser,
        currentRoster,
        FeaturePermissionLevel.MANAGE,
        FeatureNew.TASKS,
      )
      : PermissionsHelpers.canForUserFeaturePermissionRoster(
        currentUser,
        currentRoster,
        FeaturePermissionLevel.MANAGE,
        Feature.TASKS,
      );
  }, [currentUser, currentRoster, isNewPermissionsEnabled]);

  const attestationStatus = useMemo(
    () => getAttestationStatus(careJourneyDetails),
    [careJourneyDetails]
  );

  const statusColors = getStatusBadgeColors(attestationStatus);

  const { mutate: submitAttestation, isPending } = useMutation({
    mutationFn: async () =>
      carecoApi?.submitCareJourneyAttestation(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id
      ),
    onSuccess() {
      setShowModal(false);
      if (mixpanelEventProperties && currentRoster) {
        const event: MixpanelTaskEvent = {
          eventName: MixpanelEventType.BIH_ATTESTATION_COMPLETED,
          eventType: careJourneyDetails.eventType,
          careJourneyName: careJourneyDetails.journeyType,
          ...mixpanelEventProperties,
          ...getCurrentGroupDetails(currentRoster),
        };
        carecoApi?.postMixpanelEvent(event);
      }
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Attestation completed successfully.',
      });
      void queryClient.invalidateQueries({
        queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      });
    },
    onError(error: AxiosError) {
      setShowModal(false);
      Helpers.alertOnError(`Failed to submit attestation. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const invalidateCareJourneyDetails = () =>
    queryClient.invalidateQueries({
      queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
    });

  const { mutate: sendCareNote, isPending: isSendPending } = useMutation({
    mutationFn: async () =>
      carecoApi?.sendCareNote(
        careJourneyDetails.careJourneyInstanceId,
        currentRoster!.id
      ),
    onSuccess() {
      setShowSendModal(false);
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Care note is being sent.',
      });
      void invalidateCareJourneyDetails();
    },
    onError(error: AxiosError) {
      setShowSendModal(false);
      const reason = error.response?.status === HttpStatusCode.Conflict
        ? 'This Care note is already being sent.'
        : 'Failed to send Care note.';
      Helpers.alertOnError(`${reason} ${GENERICERROR} ${Helpers.traceId(error)}`, error);
      void invalidateCareJourneyDetails();
    },
  });

  const attestation: Attestation | undefined = careJourneyDetails.orchestrationEngineDetails?.attestation;
  const isNotRequired = attestationStatus === AttestationStatus.NOT_REQUIRED;
  const isCompleted = attestationStatus === AttestationStatus.COMPLETED;
  const isAttestationPending = attestationStatus === AttestationStatus.PENDING;
  const completedAttestationMessage = attestation ? buildAttestationMessage(attestation) : null;
  const canAttest = canManageTasks && isAttestationPending;
  const showAttestButton = isAttestationPending;

  const isSendCareNoteButtonVisible = currentRoster?.configuration
    .manualSendCareNoteAllowedTaskTypes?.includes(careJourneyDetails.eventType);

  const badgeIcon = attestationStatus === AttestationStatus.PENDING ? ICONS['clock'] :
    attestationStatus === AttestationStatus.NOT_READY ? ICONS['times'] :
      attestationStatus === AttestationStatus.COMPLETED ? ICONS['check'] :
        attestationStatus === AttestationStatus.NOT_REQUIRED ? ICONS['minus-circle'] : undefined;

  const subText = isNotRequired
    ? 'Attestation is not required for this event setting.'
    : isAttestationPending
      ? 'This Care Journey is ready for review and attestation.'
      : attestationStatus === AttestationStatus.NOT_READY
        ? 'Attestation will be initiated once the required step is reached.'
        : null;

  const now = new Date().toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  const modalButtons = [
    <span key="spacer" />,
    <CancelButton key="cancel" styleType={StyleType.TERTIARY} onClick={() => setShowModal(false)}>
      Cancel
    </CancelButton>,
    <Button key="confirm" onClick={() => submitAttestation()} disabled={isPending}>
      {isPending ? 'Submitting...' : 'Complete attestation'}
    </Button>,
  ];

  const sendModalButtons = [
    <span key="spacer" />,
    <CancelButton key="cancel" styleType={StyleType.TERTIARY} onClick={() => setShowSendModal(false)}>
      Cancel
    </CancelButton>,
    <Button key="confirm" onClick={() => sendCareNote()} disabled={isSendPending}>
      {isSendPending ? 'Sending...' : 'Send'}
    </Button>,
  ];

  return (
    <>
      <Section>
        <SectionContent>
          <SectionHeaderRow>
            <SectionTitleRow>
              <StandardSemiBoldTypography>Attestation</StandardSemiBoldTypography>
              <StatusBadge bgColor={statusColors.bg}>
                {badgeIcon && (
                  <StatusIcon
                    iconClass={badgeIcon}
                    weight="far"
                    iconColor={statusColors.iconColor}
                  />
                )}
                <StatusText textColor={statusColors.text}>
                  {attestationStatusDisplayValues[attestationStatus]}
                </StatusText>
              </StatusBadge>
            </SectionTitleRow>
            {showAttestButton && (
              <AttestButton
                styleType={StyleType.SECONDARY}
                canAttest={canAttest}
                onClick={() => canAttest && setShowModal(true)}
                disabled={!canAttest}
              >
                {!canManageTasks && <LockIcon iconClass={ICONS['lock-alt']} weight="far" />}
                Attest
              </AttestButton>
            )}
          </SectionHeaderRow>
          {subText && (
            <SubTextWrapper>
              <StandardTypography>{subText}</StandardTypography>
            </SubTextWrapper>
          )}
          {isCompleted && (
            <CompletedDetailsWrapper>
              <StandardSmallTypography>
                Attestation has been completed and has been included in the Care Note.
              </StandardSmallTypography>
              {completedAttestationMessage && (
                <StandardSmallTypography>{completedAttestationMessage}</StandardSmallTypography>
              )}
            </CompletedDetailsWrapper>
          )}

          <Spacer itemHeight={sizes.small} />
          <CareNotePreview careJourneyDetails={careJourneyDetails}>
            {isSendCareNoteButtonVisible && (
              <Button
                styleType={StyleType.SECONDARY}
                onClick={() => setShowSendModal(true)}
                disabled={!canManageTasks || isSendPending}
              >
                {!canManageTasks && <LockIcon iconClass={ICONS['lock-alt']} weight="far" />}
                {isSendPending ? 'Sending...' : SEND_CARE_NOTE_LABEL}
              </Button>
            )}
          </CareNotePreview>

          <Spacer itemHeight={sizes.xxSmall} />
          {careJourneyDetails.orchestrationEngineDetails?.directMessageDeliveries?.map(dm =>
            <CareNoteDeliveryStatus
              key={`${dm.attachmentId}-${dm.directAddress}`}
              directMessage={dm}
              eventType={careJourneyDetails.eventType}
              careJourneyName={careJourneyDetails.journeyType}
            />) }

        </SectionContent>
      </Section>

      {showModal && currentUser && (
        <Modal
          headerText="Complete attestation"
          hideModal={() => setShowModal(false)}
          buttons={modalButtons}
          className={css({ width: '80rem' })}
        >
          <ModalContent>
            <StandardTypography>
              I have reviewed the non-face-to-face services performed in connection with this transitional care
              management (TCM) episode. These services were performed under the supervision of the licensed
              practitioner. I attest that the documented activities were completed as recorded and that any
              clinically significant issues were appropriately identified and escalated to licensed clinical
              staff and/or the supervising practitioner, as appropriate, in accordance with established protocols.
            </StandardTypography>
            <DetailsCard>
              <DetailsTitle>
                Attestation details
              </DetailsTitle>
              <DetailsGrid>
                <div>
                  <DetailLabel>First Name</DetailLabel>
                  <StandardTypography><strong>{currentUser.first_name}</strong></StandardTypography>
                </div>
                <div>
                  <DetailLabel>Last Name</DetailLabel>
                  <StandardTypography><strong>{currentUser.last_name}</strong></StandardTypography>
                </div>
                <div>
                  <DetailLabel>Title</DetailLabel>
                  <StandardTypography><strong>{currentUser.title}</strong></StandardTypography>
                </div>
                <div>
                  <DetailLabel>Date &amp; time</DetailLabel>
                  <StandardTypography><strong>{now}</strong></StandardTypography>
                </div>
              </DetailsGrid>
            </DetailsCard>
          </ModalContent>
        </Modal>
      )}

      {showSendModal && (
        <Modal
          headerText={SEND_CARE_NOTE_LABEL}
          hideModal={() => setShowSendModal(false)}
          buttons={sendModalButtons}
          className={css({ width: '60rem' })}
        >
          <ModalContent>
            <StandardTypography>
              The Care note will be generated and sent. Delivery can take up to an hour, and any
              earlier attempts stay on this task for reference.
            </StandardTypography>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
